import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { aboutData } from '@/mocks/about';

const FORM_SUBMIT_URL = 'http://emenculture.com/';

const jsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'http://emenculture.com/' },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: 'http://emenculture.com/about' },
      ],
    },
    {
      '@type': 'AboutPage',
      name: '关于韩漫网 - 高清韩国漫画免费阅读平台',
      description: '了解韩漫网品牌故事、平台数据、核心优势及发展历程。致力于为韩漫爱好者提供最优质的高清免费阅读体验。',
      url: 'http://emenculture.com/about',
      isPartOf: { '@id': 'http://emenculture.com/#website' },
      about: { '@id': 'http://emenculture.com/#organization' },
    },
    {
      '@type': 'Organization',
      name: '韩漫网',
      url: 'http://emenculture.com/',
      description: '韩漫网站汇集众多好看的韩国漫画，全部免费观看且提供高清版本',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '400-888-6666',
        contactType: 'business cooperation',
        email: 'hello@emenculture.com',
        availableLanguage: ['Chinese'],
      },
      address: {
        '@type': 'PostalAddress',
        addressLocality: '北京',
        addressRegion: '朝阳区',
        streetAddress: '望京SOHO T1栋A座18层',
        addressCountry: 'CN',
      },
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: '韩漫网有哪些合作方式？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '韩漫网开放内容授权合作、广告投放合作及商业推广合作等多种形式，欢迎漫画作者、版权方和出版社与我们联系洽谈。',
          },
        },
        {
          '@type': 'Question',
          name: '韩漫网的漫画资源来自哪里？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '韩漫网的漫画资源来自与韩国多家知名漫画平台和出版社的正式合作授权，确保内容的正版性和质量。',
          },
        },
        {
          '@type': 'Question',
          name: '如何在韩漫网发布漫画作品？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '欢迎漫画作者通过商务合作邮箱 hello@emenculture.com 提交作品。我们的编辑团队会在3个工作日内审核并回复。',
          },
        },
      ],
    },
  ],
};

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢您的咨询，我们会尽快与您联系！');
      form.reset();
      setTimeout(() => { setFormStatus('idle'); setFormMessage(''); }, 4000);
      return;
    }

    formData.delete('phone_alt');
    setFormStatus('submitting');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value.toString());
      });

      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的咨询，我们会尽快与您联系！');
        form.reset();
      } else {
        setFormStatus('error');
        setFormMessage(parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络后重试');
    }
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <>
      <Navbar />
      <main>
        <section className="relative pt-28 pb-20 md:pt-36 md:pb-28">
          <div className="absolute inset-0">
            <img
              src={aboutData.heroImage}
              alt="关于韩漫网 - 高清韩国漫画免费阅读平台"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-background-50"></div>
          </div>
          <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-10 text-center">
            <nav className="flex items-center justify-center gap-2 text-sm text-background-50/70 mb-8 flex-wrap">
              <Link to="/" className="hover:text-background-50 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-background-50 font-medium">关于我们</span>
            </nav>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-background-50">
              {aboutData.heroTitle}
            </h1>
            <p className="mt-4 text-sm md:text-base text-background-50/80 max-w-xl mx-auto">
              {aboutData.heroSubtitle}
            </p>
          </div>
        </section>

        <section className="py-16 md:py-24">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
              <div className="lg:col-span-6">
                <p className="text-xs font-semibold text-primary-500 tracking-wider uppercase mb-3">{aboutData.brandStory.subtitle}</p>
                <h2 className="font-heading text-3xl md:text-4xl font-black text-foreground-900 mb-8">
                  {aboutData.brandStory.title}
                </h2>
                <div className="space-y-5">
                  {aboutData.brandStory.paragraphs.map((para, i) => (
                    <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed">
                      {para}
                    </p>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-6">
                <div className="relative aspect-[4/3] rounded-3xl overflow-hidden bg-background-200">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20warm%20and%20inviting%20Korean%20manhwa%20studio%20workspace%2C%20large%20windows%20with%20soft%20natural%20light%2C%20wooden%20desk%20with%20drawing%20tablet%20and%20art%20supplies%2C%20bookshelves%20filled%20with%20manhwa%20volumes%2C%20plants%20and%20cozy%20decorations%2C%20creative%20atmosphere%2C%20digital%20art%20style%20with%20warm%20cream%20tones%2C%20clean%20composition%2C%20professional%20yet%20artistic%20space&width=960&height=720&seq=about-studio-01&orientation=landscape"
                    alt="韩漫网工作室"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-background-100">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="font-heading text-3xl md:text-4xl font-black text-foreground-900">{aboutData.platformData.title}</h2>
              <p className="mt-2 text-sm text-foreground-500">{aboutData.platformData.subtitle}</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {aboutData.platformData.stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className="font-heading text-4xl md:text-5xl font-black text-primary-500 tracking-tight">
                    {stat.value}
                    <span className="text-2xl md:text-3xl">{stat.suffix}</span>
                  </p>
                  <p className="mt-2 text-sm text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="font-heading text-3xl md:text-4xl font-black text-foreground-900">{aboutData.coreValues.title}</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {aboutData.coreValues.items.map((item) => (
                <div key={item.title} className="bg-background-50 border border-background-200/70 rounded-2xl p-6 md:p-8 hover:border-primary-200 transition-colors">
                  <div className="w-12 h-12 rounded-2xl bg-primary-100 flex items-center justify-center mb-5">
                    <i className={`${item.icon} text-xl text-primary-500`}></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-background-100">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="font-heading text-3xl md:text-4xl font-black text-foreground-900">{aboutData.timeline.title}</h2>
            </div>
            <div className="max-w-3xl mx-auto relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 -translate-x-1/2"></div>
              <div className="space-y-8 md:space-y-0">
                {aboutData.timeline.items.map((item, i) => (
                  <div key={item.year} className={`relative flex items-start gap-8 md:gap-0 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className={`hidden md:block flex-1 ${i % 2 === 0 ? 'text-right pr-12' : 'text-left pl-12'}`}>
                      <div className={`inline-block ${i % 2 === 0 ? 'text-right' : 'text-left'}`}>
                        <p className="font-heading text-2xl font-black text-primary-500">{item.year}</p>
                        <p className="mt-1 text-sm text-foreground-700">{item.event}</p>
                      </div>
                    </div>
                    <div className="absolute left-4 md:left-1/2 w-4 h-4 rounded-full bg-primary-500 border-4 border-background-100 -translate-x-1/2 mt-1.5 z-10"></div>
                    <div className="flex-1 md:hidden pl-12">
                      <p className="font-heading text-xl font-black text-primary-500">{item.year}</p>
                      <p className="mt-1 text-sm text-foreground-700">{item.event}</p>
                    </div>
                    <div className="hidden md:block flex-1"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
              <div className="lg:col-span-7">
                <h2 className="font-heading text-3xl md:text-4xl font-black text-foreground-900 mb-3">{aboutData.contact.title}</h2>
                <p className="text-sm text-foreground-500 mb-2">{aboutData.contact.subtitle}</p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-8">{aboutData.contact.description}</p>

                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-primary-500"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">联系电话</p>
                      <p className="text-sm text-foreground-600">{aboutData.contact.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">商务邮箱</p>
                      <p className="text-sm text-foreground-600">{aboutData.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-secondary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">公司地址</p>
                      <p className="text-sm text-foreground-600">{aboutData.contact.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-background-200 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-foreground-500"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">工作时间</p>
                      <p className="text-sm text-foreground-600">{aboutData.contact.workHours}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="bg-background-100 rounded-3xl p-8 md:p-10">
                  <h3 className="font-heading text-xl font-bold text-foreground-900 mb-6">发送合作咨询</h3>
                  <form className="space-y-4" onSubmit={handleFormSubmit}>
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        type="text"
                        name="name"
                        className="w-full bg-background-50 border border-background-200 rounded-xl px-4 py-3 text-sm text-foreground-900 outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        type="email"
                        name="email"
                        className="w-full bg-background-50 border border-background-200 rounded-xl px-4 py-3 text-sm text-foreground-900 outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">合作意向</label>
                      <select
                        name="type"
                        className="w-full bg-background-50 border border-background-200 rounded-xl px-4 py-3 text-sm text-foreground-900 outline-none focus:border-primary-300 transition-colors cursor-pointer appearance-none"
                        defaultValue=""
                      >
                        <option value="" disabled>请选择合作类型</option>
                        <option value="content">内容授权合作</option>
                        <option value="ad">广告投放合作</option>
                        <option value="promotion">商业推广合作</option>
                        <option value="other">其他合作</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">留言</label>
                      <textarea
                        name="message"
                        rows={4}
                        maxLength={500}
                        className="w-full bg-background-50 border border-background-200 rounded-xl px-4 py-3 text-sm text-foreground-900 outline-none focus:border-primary-300 transition-colors resize-none"
                        placeholder="请描述您的合作意向..."
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      className="honeypot-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full bg-primary-500 text-background-50 py-3 rounded-xl text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {formStatus === 'submitting' ? '提交中...' : '发送咨询'}
                    </button>
                    {formMessage && (
                      <p className={`text-sm text-center mt-3 ${formStatus === 'success' ? 'text-accent-600' : 'text-primary-600'}`}>
                        {formMessage}
                      </p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}